import { DaysOfWeek } from "@/types/gql/graphql";
import { isNil } from "helper/utils";

const EMPTY_VALUE = "-";

type MaxStaySource = { maxStay?: number | null; maxStayUnit?: string | null };
type PolicyTimeSpan = {
  daysOfWeek?: DaysOfWeek[] | null;
  timeOfDayStart?: string | null;
  timeOfDayEnd?: string | null;
};

export type PolicySummarySource = {
  rules?: MaxStaySource[] | null;
  timeSpans?: PolicyTimeSpan[] | null;
};

const DAY_ORDER: DaysOfWeek[] = [
  DaysOfWeek.Mon,
  DaysOfWeek.Tue,
  DaysOfWeek.Wed,
  DaysOfWeek.Thu,
  DaysOfWeek.Fri,
  DaysOfWeek.Sat,
  DaysOfWeek.Sun,
];

const DAY_LABELS: Record<DaysOfWeek, string> = {
  [DaysOfWeek.Mon]: "lun",
  [DaysOfWeek.Tue]: "mar",
  [DaysOfWeek.Wed]: "mer",
  [DaysOfWeek.Thu]: "jeu",
  [DaysOfWeek.Fri]: "ven",
  [DaysOfWeek.Sat]: "sam",
  [DaysOfWeek.Sun]: "dim",
};

export const formatValueUnit = (value?: number | null, unit?: string | null): string => {
  if (isNil(value)) {
    return EMPTY_VALUE;
  }
  if (isNil(unit)) {
    return `${value}`;
  }
  if (value > 1) {
    return `${value} ${unit}s`;
  }
  return `${value} ${unit}`;
};

export const formatMaxStay = (rules?: MaxStaySource[] | null): string => {
  const values = rules
    ?.filter((rule) => !isNil(rule.maxStay))
    .map((rule) => formatValueUnit(rule.maxStay, rule.maxStayUnit));

  if (!values?.length) {
    return EMPTY_VALUE;
  }

  return Array.from(new Set(values)).join(", ");
};

export const formatDaysOfWeek = (timeSpans?: PolicyTimeSpan[] | null): string => {
  const selectedDays = new Set(timeSpans?.flatMap((span) => span.daysOfWeek ?? []));

  const ranges: string[] = [];
  let run: DaysOfWeek[] = [];
  for (const day of DAY_ORDER) {
    if (selectedDays.has(day)) {
      run.push(day);
    } else if (run.length) {
      ranges.push(formatDayRange(run));
      run = [];
    }
  }
  if (run.length) {
    ranges.push(formatDayRange(run));
  }

  if (!ranges.length) {
    return EMPTY_VALUE;
  }

  return ranges.join(", ");
};

const formatDayRange = (run: DaysOfWeek[]): string => {
  const first = DAY_LABELS[run[0]];
  if (run.length === 1) {
    return first;
  }
  return `${first}-${DAY_LABELS[run[run.length - 1]]}`;
};

export const formatTimeOfDay = (timeSpans?: PolicyTimeSpan[] | null): string => {
  const ranges = timeSpans
    ?.map(({ timeOfDayStart, timeOfDayEnd }) => formatTimeRange(timeOfDayStart, timeOfDayEnd))
    .filter(Boolean);

  if (!ranges?.length) {
    return EMPTY_VALUE;
  }

  return Array.from(new Set(ranges)).join(", ");
};

export const formatPolicySummary = (policy: PolicySummarySource): string => {
  const parts: string[] = [];

  const maxStay = formatMaxStay(policy.rules);
  if (maxStay !== EMPTY_VALUE) {
    parts.push(`Durée max : ${maxStay}`);
  }

  const daysOfWeek = formatDaysOfWeek(policy.timeSpans);
  if (daysOfWeek !== EMPTY_VALUE) {
    parts.push(daysOfWeek);
  }

  const timeOfDay = formatTimeOfDay(policy.timeSpans);
  if (timeOfDay !== EMPTY_VALUE) {
    parts.push(timeOfDay);
  }

  return parts.join(" • ");
};

const formatTimeRange = (start?: string | null, end?: string | null): string => {
  if (!start && !end) {
    return "";
  }
  return `${start ?? "…"} - ${end ?? "…"}`;
};
